/**
 * Force quote drawing SVG frame fills to match config colors.
 *
 * Exterior = fill of the single largest non-canvas rect (outer frame),
 * NOT the fill with the largest summed area (door leaves often win that race).
 */

import { getColorById } from '../data/ralColors';
import { detectPageType } from './vmkQuoteTable';

const FRAME_ROLES = ['exterior', 'interior', 'draaideel'];

const normalizeHex = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed === 'none' || /^url\(/i.test(trimmed)) return null;
  const rgb = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    const toHex = (n) => Number(n).toString(16).padStart(2, '0');
    return `#${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`.toUpperCase();
  }
  const match = trimmed.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!match) return null;
  let hex = match[1];
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return `#${hex.toUpperCase()}`;
};

export function resolveFrameColorHex(colorId, fallback = '#293133') {
  const hex = getColorById(colorId)?.hex;
  return normalizeHex(hex) || normalizeHex(fallback) || '#293133';
}

export function getDrawingFrameColorIds(config = {}) {
  const exterior = config.exteriorColor || config.color || 'anthracite';
  const interior = config.interiorFrameColor || config.interiorColor || 'ral-9016';
  const draaideel = config.draaideelColor || exterior || 'anthracite';
  return { exterior, interior, draaideel };
}

export function getDrawingFrameColorHexes(config = {}) {
  const ids = getDrawingFrameColorIds(config);
  return {
    exterior: resolveFrameColorHex(ids.exterior),
    interior: resolveFrameColorHex(ids.interior, '#F6F6F6'),
    draaideel: resolveFrameColorHex(ids.draaideel, resolveFrameColorHex(ids.exterior)),
  };
}

const getSvgViewBoxSize = (svg) => {
  const vb = svg.getAttribute('viewBox');
  if (vb) {
    const parts = vb.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
      return { width: parts[2], height: parts[3] };
    }
  }
  return {
    width: parseFloat(svg.getAttribute('width')) || 900,
    height: parseFloat(svg.getAttribute('height')) || 800,
  };
};

const isCanvasRect = (el, vbW, vbH) => {
  if (!el || el.tagName?.toLowerCase() !== 'rect') return false;
  const x = parseFloat(el.getAttribute('x') || '0');
  const y = parseFloat(el.getAttribute('y') || '0');
  const w = parseFloat(el.getAttribute('width') || '0');
  const h = parseFloat(el.getAttribute('height') || '0');
  if (!(w > 0 && h > 0)) return false;
  return x <= 2 && y <= 2 && w >= vbW * 0.92 && h >= vbH * 0.92;
};

const readElementFill = (el) => {
  const attr = el.getAttribute('fill');
  if (attr) return normalizeHex(attr);
  const style = el.getAttribute('style') || '';
  const match = style.match(/fill\s*:\s*([^;]+)/i);
  return match ? normalizeHex(match[1]) : null;
};

const writeElementFill = (el, hex) => {
  if (!hex || !el) return;
  if (el.hasAttribute('fill') || !(el.getAttribute('style') || '').includes('fill')) {
    el.setAttribute('fill', hex);
  }
  const style = el.getAttribute('style');
  if (style && /fill\s*:/i.test(style)) {
    el.setAttribute('style', style.replace(/fill\s*:\s*[^;]+/i, `fill: ${hex}`));
  }
};

const elementArea = (el) => {
  const tag = el.tagName?.toLowerCase();
  if (tag === 'rect') {
    const w = parseFloat(el.getAttribute('width') || '0');
    const h = parseFloat(el.getAttribute('height') || '0');
    return w > 0 && h > 0 ? w * h : 0;
  }
  return 0;
};

/** Skip near-black hardware / glazing-bead accents when clustering frame fills. */
const isLikelyAccentFill = (hex) => {
  if (!hex) return true;
  const n = hex.replace('#', '');
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max < 55 && max - min < 25) return true;
  return false;
};

/** Mid greys used for sill / U-reveal — not frame roles. */
const isLikelyStructuralGrey = (hex) => {
  if (!hex) return false;
  const n = hex.replace('#', '');
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max - min < 18 && max >= 90 && max <= 200;
};

const collectFillCandidates = (svg) => {
  const { width: vbW, height: vbH } = getSvgViewBoxSize(svg);
  const minArea = Math.max(80, vbW * vbH * 0.0008);
  const candidates = [];

  svg.querySelectorAll('rect, path, polygon, polyline, circle, ellipse').forEach((el) => {
    if (isCanvasRect(el, vbW, vbH)) return;
    const fill = readElementFill(el);
    if (!fill || isLikelyAccentFill(fill) || isLikelyStructuralGrey(fill)) return;
    const area = elementArea(el);
    if (area < minArea) return;
    candidates.push({ el, fill, area });
  });

  return candidates;
};

const paintByCurrentFill = (candidates, fromHex, toHex, role) => {
  let touched = 0;
  candidates.forEach(({ el, fill }) => {
    if (fill !== fromHex) return;
    writeElementFill(el, toHex);
    el.setAttribute('data-vmk-fill', role);
    touched += 1;
  });
  return touched;
};

/**
 * Paint frame fills to match config.
 * Exterior is taken from the largest single rect (outer frame), then that
 * fill colour is remapped — never from summed leaf-panel area.
 */
export function syncSvgFrameColorsToConfig(svgHtml, config) {
  if (!svgHtml || typeof svgHtml !== 'string') return svgHtml;
  if (typeof document === 'undefined') return svgHtml;

  const nextHexes = getDrawingFrameColorHexes(config || {});
  const pageType = detectPageType(config);
  const isDoor =
    pageType === 'voordeur' ||
    pageType === 'achterdeur' ||
    pageType === 'terrasdeur' ||
    pageType === 'schuifpui';

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgHtml, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    if (!svg || doc.querySelector('parsererror')) return svgHtml;

    // Drop stale role tags from earlier incorrect swaps so geometry can win
    svg.querySelectorAll('[data-vmk-fill]').forEach((el) => {
      el.removeAttribute('data-vmk-fill');
    });

    const candidates = collectFillCandidates(svg);
    if (!candidates.length) {
      return new XMLSerializer().serializeToString(svg);
    }

    // Outer frame = largest individual rect (full door/window outline)
    const rects = candidates
      .filter((c) => c.el.tagName.toLowerCase() === 'rect')
      .sort((a, b) => b.area - a.area);

    const outer = rects[0] || candidates.slice().sort((a, b) => b.area - a.area)[0];
    const exteriorFill = outer?.fill;

    if (exteriorFill) {
      paintByCurrentFill(candidates, exteriorFill, nextHexes.exterior, 'exterior');
    }

    // Re-read fills after exterior paint
    const afterExterior = collectFillCandidates(svg).filter(
      (c) => c.el.getAttribute('data-vmk-fill') !== 'exterior'
    );

    const areaByFill = new Map();
    afterExterior.forEach(({ fill, area }) => {
      if (fill === nextHexes.exterior) return;
      areaByFill.set(fill, (areaByFill.get(fill) || 0) + area);
    });
    const ranked = [...areaByFill.entries()].sort((a, b) => b[1] - a[1]);

    if (ranked.length && nextHexes.draaideel) {
      const [draaideelFill, draaideelArea] = ranked[0];
      const outerArea = outer?.area || 1;
      // Leaf / sash must be a meaningful panel, not a tiny accent
      if (draaideelArea >= outerArea * 0.05) {
        paintByCurrentFill(afterExterior, draaideelFill, nextHexes.draaideel, 'draaideel');
      }
    }

    if (isDoor && nextHexes.interior) {
      const leftover = collectFillCandidates(svg).filter((c) => {
        const role = c.el.getAttribute('data-vmk-fill');
        return role !== 'exterior' && role !== 'draaideel';
      });
      const interiorAreas = new Map();
      leftover.forEach(({ fill, area }) => {
        if (fill === nextHexes.exterior || fill === nextHexes.draaideel) return;
        interiorAreas.set(fill, (interiorAreas.get(fill) || 0) + area);
      });
      const interiorRanked = [...interiorAreas.entries()].sort((a, b) => b[1] - a[1]);
      if (interiorRanked.length) {
        paintByCurrentFill(leftover, interiorRanked[0][0], nextHexes.interior, 'interior');
      }
    }

    // Final pass: anything tagged must match config exactly
    svg.querySelectorAll('[data-vmk-fill]').forEach((el) => {
      const role = el.getAttribute('data-vmk-fill');
      if (!FRAME_ROLES.includes(role)) return;
      writeElementFill(el, nextHexes[role]);
    });

    return new XMLSerializer().serializeToString(svg);
  } catch {
    return svgHtml;
  }
}

/** @deprecated Prefer syncSvgFrameColorsToConfig */
export function recolorQuoteDrawingSvg(svgHtml, _prevConfig, nextConfig) {
  return syncSvgFrameColorsToConfig(svgHtml, nextConfig);
}
